import Axhelper from '@/lib/Axhelper'

class KampanieService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG.API_URL
		this.endpoints = {
			kampanieFilters: '/api/kampanie/filters',
			kampanieDetails: '/api/kampanie/details',
			kampanieCreate: '/api/kampanie/add',
			kampanieCreateWithFilter: '/api/kampanie/addWithFilter',
			kampanieUpdate: '/api/kampanie',
			kampanieDelete: '/api/kampanie',
		}
	}

	_buildPlacowkaFilterQueryParams(searchText = '', filters = {}) {
		const params = new URLSearchParams()
		const trimmedSearch = (searchText || '').trim()
		if (trimmedSearch) {
			params.append('searchText', trimmedSearch)
		}
		if (filters?.id_placowka) {
			params.append('id_placowka', String(filters.id_placowka))
		}
		if (filters?.id_miasto) {
			params.append('id_miasto', String(filters.id_miasto))
		}
		if (filters?.id_gmina) {
			params.append('id_gmina', String(filters.id_gmina))
		}
		if (filters?.id_powiat) {
			params.append('id_powiat', String(filters.id_powiat))
		}
		if (filters?.id_wojewodztwo) {
			params.append('id_wojewodztwo', String(filters.id_wojewodztwo))
		}
		if (filters?.id_typ) {
			params.append('id_typ', String(filters.id_typ))
		}
		return params
	}

	_buildPlacowkiCreatePayload(kampanieData, placowki) {
		const params = new URLSearchParams()
		if (kampanieData?.nazwa) {
			params.append('nazwa', kampanieData.nazwa)
		}
		if (kampanieData?.description != null) {
			params.append('description', kampanieData.description)
		}
		const list = Array.isArray(placowki) ? placowki : []
		list.forEach((placowka, index) => {
			const placowkaId = placowka?._id ?? placowka?.id ?? placowka?.placowkaId ?? placowka
			if (placowkaId) {
				params.append(`placowki[${index}][placowkaId]`, String(placowkaId))
			}
		})
		return params
	}

	_buildFilterParams(options = {}) {
		const {
			limit = 50,
			sortColumn = '_id',
			sortDirection = 'desc',
			searchText = '',
			filters = {},
		} = options

		const params = new URLSearchParams({
			sortcol: sortColumn,
			sortdir: sortDirection,
			limit: String(limit),
		})

		const trimmedSearch = (searchText || '').trim()
		if (trimmedSearch) {
			params.append('searchText', trimmedSearch)
		}
		if (filters?.placowkaId) {
			params.append('placowkaId', String(filters.placowkaId))
		}
		if (filters?.createdById) {
			params.append('createdById', String(filters.createdById))
		}

		return params
	}

	_parseListResponse(response, rows = null) {
		const data = rows ?? (Array.isArray(response.data) ? response.data : [])
		const totalCount = response.headers?.['x-total-count']
			? parseInt(response.headers['x-total-count'], 10)
			: data.length

		return {
			data,
			error: null,
			totalCount,
			hasMore: data.length < totalCount,
			headers: response.headers,
		}
	}

	async getFiltered(options = {}) {
		try {
			const params = this._buildFilterParams(options)
			const url = `${this.endpoints.kampanieFilters}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			return this._parseListResponse(response)
		} catch (error) {
			console.error('KampanieService.getFiltered error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch kampanie',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async getAll(options = {}) {
		return this.getFiltered(options)
	}

	async getById(kampanieId) {
		try {
			if (!kampanieId) {
				return {
					data: null,
					error: 'Invalid kampanie ID provided',
				}
			}

			const url = `${this.endpoints.kampanieDetails}/${encodeURIComponent(kampanieId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: null,
					error: response.error,
				}
			}

			return {
				data: response.data || null,
				error: null,
			}
		} catch (error) {
			console.error('KampanieService.getById error:', error)
			return {
				data: null,
				error: error.message || 'Failed to fetch kampanie details',
			}
		}
	}

	async search(searchTerm, options = {}) {
		try {
			const trimmed = (searchTerm || '').trim()
			if (!trimmed) {
				return {
					data: [],
					error: 'Invalid search term provided',
					totalCount: 0,
					hasMore: false,
				}
			}

			const params = this._buildFilterParams({
				...options,
				searchText: trimmed,
			})
			const url = `${this.endpoints.kampanieFilters}?${params.toString()}`

			const looksLikeObjectId = /^[a-fA-F0-9]{24}$/.test(trimmed)
			const [response, byIdResult] = await Promise.all([
				Axhelper.sendGetAsync(url),
				looksLikeObjectId ? this.getById(trimmed) : Promise.resolve({ data: null, error: null }),
			])

			let rows = []
			if (!response.error) {
				rows = Array.isArray(response.data) ? [...response.data] : []
			}

			if (looksLikeObjectId && byIdResult?.data && !byIdResult.error) {
				const doc = byIdResult.data
				const docId = doc?._id ?? doc?.id
				const already = rows.some(item => String(item?._id ?? item?.id) === String(docId))
				if (!already) {
					rows.unshift(doc)
				}
			}

			if (response.error && rows.length === 0) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			return this._parseListResponse(response, rows)
		} catch (error) {
			console.error('KampanieService.search error:', error)
			return {
				data: [],
				error: error.message || 'Failed to search kampanie',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async create(kampanieData) {
		try {
			const response = await Axhelper.sendPostAsync(this.endpoints.kampanieCreate, kampanieData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('KampanieService.create error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create kampanie',
			}
		}
	}

	async createFromPlacowki(kampanieData, placowki) {
		try {
			const list = (Array.isArray(placowki) ? placowki : []).filter(Boolean)
			if (list.length === 0) {
				return {
					success: false,
					error: 'Nie wybrano zadnych placowek',
					data: null,
				}
			}

			const payload = this._buildPlacowkiCreatePayload(kampanieData, list)
			const response = await Axhelper.sendPostAsync(this.endpoints.kampanieCreate, payload)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					data: null,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('KampanieService.createFromPlacowki error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create kampanie with placowki',
				data: null,
			}
		}
	}

	async createWithFilter(kampanieData, options = {}) {
		try {
			const { searchText = '', filters = {} } = options
			const queryParams = this._buildPlacowkaFilterQueryParams(searchText, filters)
			const url = `${this.endpoints.kampanieCreateWithFilter}?${queryParams.toString()}`
			const payload = new URLSearchParams()
			if (kampanieData?.nazwa) {
				payload.append('nazwa', kampanieData.nazwa)
			}
			if (kampanieData?.description != null) {
				payload.append('description', kampanieData.description)
			}

			const response = await Axhelper.sendPostAsync(url, payload)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					data: null,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('KampanieService.createWithFilter error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create kampanie with filter',
				data: null,
			}
		}
	}

	async update(kampanieId, kampanieData) {
		try {
			if (!kampanieId) {
				return {
					success: false,
					error: 'Invalid kampanie ID provided',
				}
			}

			const url = `${this.endpoints.kampanieUpdate}/${encodeURIComponent(kampanieId)}`
			const response = await Axhelper.sendPutAsync(url, kampanieData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('KampanieService.update error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update kampanie',
			}
		}
	}

	async delete(kampanieId) {
		try {
			if (!kampanieId) {
				return {
					success: false,
					error: 'Invalid kampanie ID provided',
					message: null,
				}
			}

			const url = `${this.endpoints.kampanieDelete}/${encodeURIComponent(kampanieId)}`
			const response = await Axhelper.sendDeleteAsync(url)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					message: null,
				}
			}

			return {
				success: true,
				error: null,
				message: response.data || 'Kampanie deleted successfully',
			}
		} catch (error) {
			console.error('KampanieService.delete error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete kampanie',
				message: null,
			}
		}
	}
}

export default new KampanieService()
